import { SubjectModel } from '@/models/taxonomy/subject';
import { ClassificationModel } from '@/models/taxonomy/classification';
import { MicroCompetencyModel } from '@/models/content/micro-competency';
import { TaxonomyCode } from '@/models/taxonomy/code';
import { CODE_TYPES } from '@/utils/constants';
import { isEmptyValue } from '@/utils/utils';

export class TaxonomySerializer {

  private static INSTANCE = new TaxonomySerializer();

  private STANDARDS_BASE_LEVEL = 3;

  static get instance() {
    return this.INSTANCE;
  }

  public serializeTaxonomyClassifications(taxonomyClassifications: any) {
    return taxonomyClassifications ? taxonomyClassifications.subject_classifications : [];
  }

  public serializeTaxonomySubjects(taxonomySubjects: any) {
    return taxonomySubjects ? taxonomySubjects.subjects : [];
  }

  public serializeTaxonomyCourse(taxonomySubjects: any) {
    return taxonomySubjects ? taxonomySubjects.courses : [];
  }

  // public serializeTaxonomyCompatency(taxonomyCompatency: any) {
  //   return taxonomyCompatency ? taxonomyCompatency.compatency : [];
  // }

  public serializeTaxonomyGrades(taxonomyGrades: any) {
    return taxonomyGrades ? taxonomyGrades.grades : [];
  }

  public serializeTaxonomyGradeBoundaries(gradeBoundaries: any) {
    return gradeBoundaries ? gradeBoundaries.domains : [];
  }

  public serializeTaxonomyFrameWork(payload: any) {
    return payload ? payload.frameworks : [];
  }

  public serializeTaxonomyCrosseWalkFC(payload: any) {
    return payload ? payload.competencyMatrix : [];
  }

  public normalizefetchCode(payload: any): MicroCompetencyModel[] {
    const result: MicroCompetencyModel[] = [];
    if (payload.codes) {
      payload.codes.map((codes: any) => {
        result.push({
          id: codes.id,
          code: codes.code,
          codeType: codes.code_type,
          isSelectable: codes.is_selectable,
          sequenceId: codes.sequence_id,
          title: codes.title,
        });
      });
    }
    return result;
  }

  public serializeTaxonomyCodes(payload: any): TaxonomyCode[] {
    const taxonomyCodes = payload.codes || [];
    const serializedTaxonomyCodes: TaxonomyCode[] = taxonomyCodes.map((taxonomyCode: TaxonomyCode) => {
      const serializedTaxonomyCode: TaxonomyCode = {
        code: taxonomyCode.code,
        code_type: taxonomyCode.code_type,
        id: taxonomyCode.id,
        is_selectable: taxonomyCode.is_selectable,
        parent_taxonomy_code_id: taxonomyCode.parent_taxonomy_code_id,
        sequence_id: taxonomyCode.sequence_id,
        title: taxonomyCode.title,
      };
      return serializedTaxonomyCode;
    });
    return serializedTaxonomyCodes;
  }

  /**
   * Normalized data of user competencies
   * @return {Object}
   */
  public normalizeCrosswalkCompetencyData(id: string, response: any) {
    response = response.competencyMatrix;
    const competenciesAndMicro: any = [];
    response.forEach((data: any) => {
      const competencies = data.competencies;
      competencies.forEach((competency: any) => {
        competenciesAndMicro.push(
          this.parseCompetencyData(competency),
        );
        if (competency.microCompetencies) {
          const microCompetencies = competency.microCompetencies;
          microCompetencies.forEach((microCompetency: any) => {
            competenciesAndMicro.push(
              this.parseMicroCompetencyData(microCompetency),
            );
          });
        }
      });
    });
    return {
      id,
      data: competenciesAndMicro,
    };
  }

  public parseCompetencyData(competency: any) {
    return {
      id: competency.frameworkCompetencyCode,
      title: competency.frameworkCompetencyName,
      key: competency.competencyCode,
      displayCode: competency.frameworkCompetencyDisplayCode,
      isMicro: false,
    };
  }

  public parseMicroCompetencyData(microCompetency: any) {
    return {
      id: microCompetency.fwMcompCode,
      title: microCompetency.fwMcompName,
      key: microCompetency.txMcompCode,
      isMicro: true,
    };
  }
  public createStandardsHierarchy(codes: any) {
    const sortedTaxonomyItems = this.sortCodes(codes);
    let standardsWithoutCategory;
    let standardsCategories;

    this.attachChildren(
      sortedTaxonomyItems[3],
      3,
      sortedTaxonomyItems[2],
      sortedTaxonomyItems[1],
    );
    this.attachChildren(
      sortedTaxonomyItems[2],
      2,
      sortedTaxonomyItems[1],
      sortedTaxonomyItems[0],
    );
    standardsWithoutCategory = this.attachStandards(
      sortedTaxonomyItems[1],
      sortedTaxonomyItems[0],
      [],
    );

    standardsCategories = this.attachStandardsWithoutCategory(
      standardsWithoutCategory,
      sortedTaxonomyItems[0],
    );
    return standardsCategories;
  }

  public attachChildren(children: any, levelOffset: any, firstLevelParents: any, secondLevelParents: any) {
    const BASE_LEVEL = this.STANDARDS_BASE_LEVEL; // standards base level
    if (children.length) {
      const siblings = [];
      const remaining: any = [];
      const lead = children.pop();
      const parentId = lead.parent; // parentTaxonomyCodeId
      let parent = firstLevelParents.find((item: any) => item.id === parentId);
      children.forEach((taxonomyItem: any) => {
        if (taxonomyItem.parent === parentId) {
          siblings.push(taxonomyItem);
        } else {
          remaining.push(taxonomyItem);
        }
      });

      if (!parent) {
        const grandparent = secondLevelParents.find((item: any) => item.id === parentId);

        if (grandparent) {
          // Use a "fake" parent to close any gaps in the hierarchy
          parent = {
            id: `empty-${parentId}`,
            level: BASE_LEVEL + (levelOffset - 1), // Level for fake parent
            parent: grandparent, // Save reference to standard
          };
          grandparent.children = [parent].concat(grandparent.children);
          grandparent.children = grandparent.children.filter((item: any) => item);
        } else {
          this.attachChildren(
            remaining,
            levelOffset,
            firstLevelParents,
            secondLevelParents,
          );
        }
      }

      // Add the lead in with its siblings
      siblings.push(lead);
      siblings.forEach((taxonomyItem) => {
        taxonomyItem.parent = parent;
      });

      // Concat the list of children with any existing children the parent may already have
      parent.children = siblings.concat(parent.children);
      parent.children = parent.children.filter((item: any) => item);
      this.attachChildren(
        remaining,
        levelOffset,
        firstLevelParents,
        secondLevelParents,
      );
    }
  }

  public attachStandards(standards: any, categories: any, listWithoutCategory: any): any {
    if (!standards.length) {
      return listWithoutCategory;
    } else {
      const lead = standards.pop();
      if (!lead.parent) {
        listWithoutCategory.unshift(lead);
        return this.attachStandards(standards, categories, listWithoutCategory);
      } else {
        const siblings = [];
        const remaining: any = [];
        const parentId = lead.parent;
        const parent = categories.find((item: any) => item.id === parentId);

        standards.forEach((taxonomyItem: any) => {
          if (taxonomyItem.parent === parentId) {
            siblings.push(taxonomyItem);
          } else {
            remaining.push(taxonomyItem);
          }
        });

        if (!parent) {
          return this.attachStandards(
            remaining,
            categories,
            listWithoutCategory,
          );
        }

        // Add the lead in with its siblings
        siblings.push(lead);
        siblings.forEach((taxonomyItem) => {
          taxonomyItem.parent = parent;
        });

        // Concat the list of children with any existing children the parent may already have
        parent.children = siblings.concat(parent.children);
        parent.children = parent.children.filter((item: any) => item);
        return this.attachStandards(remaining, categories, listWithoutCategory);
      }
    }
  }

  public attachStandardsWithoutCategory(standards: any, categories: any) {
    const BASE_LEVEL = this.STANDARDS_BASE_LEVEL; // standards base level

    const defaultCategory = {
      id: 'empty-category',
      level: BASE_LEVEL,
      children: standards,
    };

    standards.forEach((taxonomyItem: any) => {
      taxonomyItem.parent = defaultCategory;
    });

    categories.push(defaultCategory);
    return categories;
  }

  public sortCodes(codes: any) {
    const BASE_LEVEL = this.STANDARDS_BASE_LEVEL; // standards base level
    const NUM_BUCKETS = 4;
    const codesLen = codes.length;
    const buckets: any = [];

    for (let i = NUM_BUCKETS - 1; i >= 0; --i) {
      // Make an array of arrays to store the different levels of standards
      buckets[i] = [];
    }

    for (let i = 0; i < codesLen; i++) {
      const code = codes[i];
      // NOTE: temporarily assign the parentTaxonomyCodeId to the parent property
      // It will be replaced in the next phase of the process.
      const taxonomyItem: any = {
        id: code.id,
        code: code.code,
        codeType: code.code_type,
        title: code.title,
        parent: code.parent_taxonomy_code_id,
      };

      switch (code.code_type) {
        case CODE_TYPES.STANDARD_CATEGORY:
          taxonomyItem.level = BASE_LEVEL;
          buckets[0].push(taxonomyItem);
          break;
        case CODE_TYPES.STANDARD:
          taxonomyItem.level = BASE_LEVEL + 1;
          buckets[1].push(taxonomyItem);
          break;
        case CODE_TYPES.SUB_STANDARD:
          taxonomyItem.level = BASE_LEVEL + 2;
          buckets[2].push(taxonomyItem);
          break;
        case CODE_TYPES.LEARNING_TARGET_L0:
        case CODE_TYPES.LEARNING_TARGET_L1:
        case CODE_TYPES.LEARNING_TARGET_L2:
          taxonomyItem.level = BASE_LEVEL + 3;
          buckets[3].push(taxonomyItem);
          break;
        default:
          break;
      }
    }
    return buckets;
  }

  public normalizeCodeCompatencyBychild(list: any) {
    const compatencyMainList = [];
    const compatencyChildList = [];
    const remaining = [];
    if (list && list.length > 0) {
      for (const iterator of list) {
        if (isEmptyValue(iterator.parent_taxonomy_code_id)) {
          compatencyMainList.push(iterator);
        } else {
          compatencyChildList.push(iterator);
        }
      }
    }

    if (compatencyChildList && compatencyChildList.length > 0) {
      for (const iteratorChild of compatencyChildList) {
        const itemIndex = compatencyMainList.findIndex((item: any) => {
          return item.id === iteratorChild.parent_taxonomy_code_id;
        });
        if (itemIndex === -1) {
          remaining.push(iteratorChild);
        } else {
          if (compatencyMainList[itemIndex].children && compatencyMainList[itemIndex].children.length > 0) {
            compatencyMainList[itemIndex].children.push(iteratorChild);
          } else {
            compatencyMainList[itemIndex].children = [];
            compatencyMainList[itemIndex].children.push(iteratorChild);
          }
        }
      }
    }

    if (remaining && remaining.length > 0) {
      for (const iteratorCRemain of remaining) {
        compatencyMainList.push(iteratorCRemain);
      }
    }

    return compatencyMainList;
  }
}

export const taxonomySerializer = TaxonomySerializer.instance;
